import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Users,
  UserPlus,
  Flame,
  Clock,
  Eye,
  MessageCircle,
  Phone,
  Mail,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Target,
  Filter,
  RefreshCw,
  ChevronRight,
  User,
  Calendar,
  ArrowLeft,
  UserCheck,
  UserX,
  Search,
  X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lead status configuration
const STATUS_CONFIG = {
  hot: { label: "Caliente", color: "bg-red-500", textColor: "text-red-700", bgColor: "bg-red-50", icon: Flame },
  warm: { label: "Tibio", color: "bg-amber-500", textColor: "text-amber-700", bgColor: "bg-amber-50", icon: TrendingUp },
  new: { label: "Nuevo", color: "bg-blue-500", textColor: "text-blue-700", bgColor: "bg-blue-50", icon: User },
  contacted: { label: "Contactado", color: "bg-purple-500", textColor: "text-purple-700", bgColor: "bg-purple-50", icon: MessageCircle },
  converted: { label: "Convertido", color: "bg-emerald-500", textColor: "text-emerald-700", bgColor: "bg-emerald-50", icon: CheckCircle2 }
};

// Lead Card Component
const LeadCard = ({ lead, vendors, onAssign, isAssigned }) => {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [assigning, setAssigning] = useState(false);
  
  const status = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
  const StatusIcon = status.icon;
  
  const handleAssign = async () => {
    if (!selectedVendor) {
      toast.error("Selecciona un vendedor");
      return;
    }
    
    setAssigning(true);
    try {
      await onAssign(lead.user_id, selectedVendor);
      setShowAssignModal(false);
      toast.success("Lead asignado correctamente");
    } catch (error) {
      toast.error("Error al asignar lead");
    } finally {
      setAssigning(false);
    }
  };
  
  const assignedVendor = vendors.find(v => v.id === lead.assigned_to);
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-white rounded-xl border ${status.bgColor} ${isAssigned ? 'border-emerald-200' : 'border-amber-200'} p-4 hover:shadow-md transition-shadow`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${status.color} flex items-center justify-center`}>
              <StatusIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-navy-900">{lead.name || "Sin nombre"}</h3>
              <p className="text-sm text-navy-500">{lead.email}</p>
            </div>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-bold ${status.bgColor} ${status.textColor}`}>
            {status.label}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
          <div className="flex items-center gap-1 text-navy-600">
            <Phone className="w-3 h-3" />
            {lead.phone || "Sin teléfono"}
          </div>
          <div className="flex items-center gap-1 text-navy-600">
            <Target className="w-3 h-3" />
            {lead.interest_score || 0} puntos
          </div>
          <div className="flex items-center gap-1 text-navy-600">
            <Calendar className="w-3 h-3" />
            {new Date(lead.created_at).toLocaleDateString('es-MX')}
          </div>
          <div className="flex items-center gap-1 text-navy-600">
            <Eye className="w-3 h-3" />
            {Object.keys(lead.page_views || {}).length} páginas
          </div>
        </div>
        
        {isAssigned && assignedVendor && (
          <div className="flex items-center gap-2 mb-3 p-2 bg-emerald-50 rounded-lg">
            <UserCheck className="w-4 h-4 text-emerald-600" />
            <span className="text-sm text-emerald-700">
              Asignado a: <strong>{assignedVendor.name}</strong>
            </span>
          </div>
        )}
        
        <div className="flex gap-2">
          {!isAssigned ? (
            <Button
              onClick={() => setShowAssignModal(true)}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              size="sm"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Asignar
            </Button>
          ) : (
            <Button
              onClick={() => setShowAssignModal(true)}
              variant="outline"
              className="flex-1"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Reasignar
            </Button>
          )}
        </div>
      </motion.div>
      
      {/* Assign Modal */}
      <AnimatePresence>
        {showAssignModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAssignModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-navy-900">
                  {isAssigned ? "Reasignar Lead" : "Asignar Lead"}
                </h2>
                <button onClick={() => setShowAssignModal(false)} className="p-2 hover:bg-navy-100 rounded-lg">
                  <X className="w-5 h-5 text-navy-500" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-navy-50 rounded-lg">
                <p className="font-medium text-navy-900">{lead.name || lead.email}</p>
                <p className="text-sm text-navy-600">{lead.email}</p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Seleccionar Vendedor
                </label>
                <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                  <SelectTrigger>
                    <SelectValue placeholder="Elige un vendedor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name} ({vendor.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowAssignModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleAssign} disabled={assigning || !selectedVendor} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {assigning ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <UserCheck className="w-4 h-4 mr-2" />
                  )}
                  {isAssigned ? "Reasignar" : "Asignar"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("unassigned");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/trial-leads/leads?limit=100`),
        axios.get(`${API}/admin/staff`)
      ]);
      
      // Ensure leads is always an array
      const leadsData = Array.isArray(leadsRes.data) ? leadsRes.data : [];
      setLeads(leadsData);
      
      // Filter only vendors from staff
      const staffData = Array.isArray(vendorsRes.data) ? vendorsRes.data : [];
      setVendors(staffData.filter(s => s.role === "vendedor"));
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
      setLeads([]);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignLead = async (leadId, vendorId) => {
    await axios.post(`${API}/trial-leads/leads/${leadId}/assign`, {
      vendedor_id: vendorId
    });
    await fetchData(); // Refresh data
  };

  // Separate leads into assigned and unassigned
  const unassignedLeads = leads.filter(lead => !lead.assigned_to && !lead.vendedor_id);
  const assignedLeads = leads.filter(lead => lead.assigned_to || lead.vendedor_id);

  // Apply filters
  const filterLeads = (leadsList) => {
    return leadsList.filter(lead => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!lead.name?.toLowerCase().includes(query) && 
            !lead.email?.toLowerCase().includes(query) &&
            !lead.phone?.includes(query)) {
          return false;
        }
      }
      // Status filter
      if (filterStatus !== "all" && lead.status !== filterStatus) {
        return false;
      }
      return true;
    });
  };

  const displayedLeads = activeTab === "unassigned" 
    ? filterLeads(unassignedLeads) 
    : filterLeads(assignedLeads);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to="/admin" className="p-2 hover:bg-navy-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-navy-500" />
              </Link>
              <h1 className="font-heading text-3xl font-bold text-navy-900">Gestión de Leads</h1>
            </div>
            <p className="text-navy-500 ml-12">Asigna leads a vendedores</p>
          </div>
          <Button onClick={fetchData} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-navy-100">
            <div className="flex items-center gap-2 text-navy-600 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total Leads</span>
            </div>
            <p className="text-2xl font-bold text-navy-900">{leads.length}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center gap-2 text-amber-700 mb-1">
              <UserX className="w-4 h-4" />
              <span className="text-sm">Sin Asignar</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{unassignedLeads.length}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 text-emerald-700 mb-1">
              <UserCheck className="w-4 h-4" />
              <span className="text-sm">Asignados</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{assignedLeads.length}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <div className="flex items-center gap-2 text-purple-700 mb-1">
              <Target className="w-4 h-4" />
              <span className="text-sm">Vendedores</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">{vendors.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("unassigned")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "unassigned"
                ? "bg-amber-100 text-amber-700 border-2 border-amber-300"
                : "bg-navy-50 text-navy-600 border-2 border-transparent hover:border-navy-200"
            }`}
          >
            <UserX className="w-4 h-4" />
            Sin Asignar ({unassignedLeads.length})
          </button>
          <button
            onClick={() => setActiveTab("assigned")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "assigned"
                ? "bg-emerald-100 text-emerald-700 border-2 border-emerald-300"
                : "bg-navy-50 text-navy-600 border-2 border-transparent hover:border-navy-200"
            }`}
          >
            <UserCheck className="w-4 h-4" />
            Asignados ({assignedLeads.length})
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-navy-400" />
            <Input
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="new">Nuevo</SelectItem>
              <SelectItem value="warm">Tibio</SelectItem>
              <SelectItem value="hot">Caliente</SelectItem>
              <SelectItem value="contacted">Contactado</SelectItem>
              <SelectItem value="converted">Convertido</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Leads Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-navy-400 animate-spin" />
          </div>
        ) : displayedLeads.length === 0 ? (
          <div className="text-center py-12 bg-navy-50 rounded-xl">
            <Users className="w-12 h-12 text-navy-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-navy-700 mb-1">
              {activeTab === "unassigned" ? "No hay leads sin asignar" : "No hay leads asignados"}
            </h3>
            <p className="text-navy-500">
              {activeTab === "unassigned" 
                ? "Todos los leads han sido asignados a vendedores"
                : "Los leads aparecerán aquí cuando sean asignados"
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayedLeads.map(lead => (
              <LeadCard
                key={lead.user_id}
                lead={lead}
                vendors={vendors}
                onAssign={handleAssignLead}
                isAssigned={activeTab === "assigned"}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminLeads;
